/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {
  makeStyles,
  Tooltip
} from '@material-ui/core';
import {viewDeviceById} from '@ion/applications/src/pages/devices/state';
import {useEventListener} from '@ion/core/src/hooks/useEventListener';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Link} from '@ion/core/src/types/entities';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {
  dlsAlt,
  dlsColor,
  dlsTypography
} from '@ion/dls/src/theme/theme';
import DeviceTitle from './DeviceTitle';

export interface DeviceLinkType {
  data: GridResult;
  isEditMode?: boolean;
  useCTA?: boolean;
  preventParentEventTrigger?: boolean;
};

const useStyles = makeStyles(() => ({
  deviceSection : {
    display : 'flex'
  },
  deviceSections : {
    display       : 'flex',
    alignItems    : 'center',
    letterSpacing : '0.4px'
  },
  deviceName : {
    cursor         : 'pointer',
    textDecoration : 'underline'
  },
  moreDevices : {
    color : dlsColor('grey3')
  },
  descriptionIcon : {
    marginRight : '8px'
  },
  selector : {
    color      : dlsColor('grey3'),
    '&::after' : {
      marginRight : '4px',
      content     : '", "'
    }
  },
  ctaLink : {
    ...dlsTypography('cta'),
    color          : dlsColor('grey1'),
    cursor         : 'pointer',
    letterSpacing  : '1px',
    lineHeight     : 'inherit',
    fontSize       : 'inherit',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    textDecoration : 'underline'
  }
}));

const deviceTooltip = makeStyles(() => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'reportTooltip')
  },
  popper : {
    width         : 'auto',
    pointerEvents : 'auto'
  }
}));

/**
 * Defines the DeviceLink component. This component is used to
 * display the view device for the selected Asset
 *
 * ```typescript
 *
 *   import DeviceLink from './DeviceLink';
 *
 *   <IonGrid  columnComponents={{
 *     deviceCell : DeviceLink
 *   }} />
 *
 * ```
 */
export const DeviceLink = ({
  data,
  isEditMode,
  useCTA = false,
  preventParentEventTrigger = true
}: DeviceLinkType = {data: {}}) => {
  const dispatch                      = useDispatch();
  const classes                       = useStyles();
  const devices                       = data?.devices;
  const deviceLength                  = devices?.length;
  const [openDevices, setOpenDevices] = useState(false);

  useEventListener(
    'scroll',
    useCallback(() => {
      openDevices && setOpenDevices(false);
    }, [openDevices]),
    document.getElementsByClassName('ag-body-viewport')?.[0] as any
  );

  const moreDevicesText = useMemo(() => {
    if (deviceLength > 1) {
      return translate('admin-devices:grid.moreDevices', {deviceLength: deviceLength - 1});
    }
    return '';
  }, [deviceLength]);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      preventParentEventTrigger && e?.stopPropagation?.();
      setOpenDevices(open);
    };
  }, [preventParentEventTrigger]);

  const onDeviceClick = useCallback((device: Link) => {
    toggleTooltip()((e) => e);
    dispatch(viewDeviceById(entityId(device?.href), true, isEditMode) as any);
  }, [toggleTooltip, isEditMode]);

  const onFirstDeviceClick = useCallback(() => {
    onDeviceClick(devices[0]);
  }, [devices, onDeviceClick]);

  if (data) {
    return (
      <ClickAwayListener onClickAway={toggleTooltip()}
        mountListener>
        <Tooltip arrow
          classes={deviceTooltip()}
          open={openDevices}
          title={
            <DeviceTitle devices={devices}
              onDeviceClick={onDeviceClick} />
          }
          placement='top'>
          <div className={classes.deviceSection}>
            {useCTA ? (
              <CTALink className={classes.ctaLink}
                onClick={onFirstDeviceClick}>
                {devices?.[0]?.title}
              </ CTALink>) : (
              <div className={classes.deviceName}
                onClick={onFirstDeviceClick}>
                {devices?.[0]?.title}
              </div>
            )}
            {deviceLength > 1 && (
              <>
                <div className={classes.selector} />
                <div className={classes.moreDevices}
                  onClick={toggleTooltip(!openDevices)}>
                  <span className={classes.deviceName}>{moreDevicesText}</span>
                </div>
              </>
            )}
          </div>
        </Tooltip>
      </ClickAwayListener>
    );
  }
};

export default React.memo(DeviceLink);
